const API_BASE = '/api';

const getToken = () => localStorage.getItem('token');

const handleResponse = async (response) => {
  const text = await response.text();
  if (!text) {
    if (!response.ok) {
      throw new Error('Empty response with error status: ' + response.status);
    }
    return {};
  }
  const data = JSON.parse(text);
  if (!response.ok) {
    throw new Error(data.error || 'Something went wrong');
  }
  return data;
};

export const api = {
  // Auth
  async login(username, password) {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    return handleResponse(response);
  },

  async register(username, password) {
    const response = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    return handleResponse(response);
  },

  // User
  async getCurrentUser() {
    const response = await fetch(`${API_BASE}/user`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  async changePassword(oldPassword, newPassword) {
    const response = await fetch(`${API_BASE}/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
    });
    return handleResponse(response);
  },

  // Projects
  async getProjects() {
    const response = await fetch(`${API_BASE}/projects`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  async getMyProjects() {
    const response = await fetch(`${API_BASE}/projects/my`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  // Settings
  async getSettings() {
    const response = await fetch(`${API_BASE}/settings`, {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    return handleResponse(response);
  },

  async updateSettings(settings) {
    const response = await fetch(`${API_BASE}/settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      body: JSON.stringify(settings)
    });
    return handleResponse(response);
  },

  async sendTestEmail() {
    const response = await fetch(`${API_BASE}/settings/test-email`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    return handleResponse(response);
  },

  // Backups
  async getBackups() {
    const response = await fetch(`${API_BASE}/backups`, {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    return handleResponse(response);
  },

  async createBackup() {
    const response = await fetch(`${API_BASE}/backups`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    return handleResponse(response);
  },

  async deleteBackup(filename) {
    const response = await fetch(`${API_BASE}/backups/${filename}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    return handleResponse(response);
  },

  async createProject(data) {
    const response = await fetch(`${API_BASE}/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async updateProject(id, data) {
    const response = await fetch(`${API_BASE}/projects/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async deleteProject(id) {
    const response = await fetch(`${API_BASE}/projects/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  async getProjectMembers(projectId) {
    const response = await fetch(`${API_BASE}/projects/${projectId}/members`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  async addProjectMember(projectId, userId) {
    const response = await fetch(`${API_BASE}/projects/${projectId}/members`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ user_id: userId }),
    });
    return handleResponse(response);
  },

  async removeProjectMember(projectId, userId) {
    const response = await fetch(`${API_BASE}/projects/${projectId}/members/${userId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  // User Management
  async getUsers() {
    const response = await fetch(`${API_BASE}/users`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  async createUser(userData) {
    const response = await fetch(`${API_BASE}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  async updateUser(username, userData) {
    const response = await fetch(`${API_BASE}/users/${username}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  async deleteUser(username) {
    const response = await fetch(`${API_BASE}/users/${username}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  // Receipts
  async getReceipts(page = 1, limit = 10, sort = 'date_desc', filter = 'all', filterId = '', filterConcept = '', filterPayer = '', filterRecipient = '', dateFrom = '', dateTo = '', projectId = null) {
    let url = `${API_BASE}/receipts?page=${page}&limit=${limit}&sort=${sort}&filter=${filter}`;
    if (filterId) url += `&id=${encodeURIComponent(filterId)}`;
    if (filterConcept) url += `&concept=${encodeURIComponent(filterConcept)}`;
    if (filterPayer) url += `&payer=${encodeURIComponent(filterPayer)}`;
    if (filterRecipient) url += `&recipient=${encodeURIComponent(filterRecipient)}`;
    if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
    if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;
    if (projectId) url += `&project_id=${projectId}`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  async getReceipt(id) {
    const response = await fetch(`${API_BASE}/receipts/${id}`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  async createReceipt(data, projectId = null) {
    let url = `${API_BASE}/receipts`;
    if (projectId) url += `?project_id=${projectId}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async updateReceipt(id, data) {
    const response = await fetch(`${API_BASE}/receipts/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },


  async createQuote(data, projectId) {
    const payload = { ...data, project_id: projectId };
    const response = await fetch(`${API_BASE}/quotes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      body: JSON.stringify(payload)
    });
    return handleResponse(response);
  },
  async deleteReceipt(id) {
    const response = await fetch(`${API_BASE}/receipts/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  async cancelReceipt(id, reason) {
    const response = await fetch(`${API_BASE}/receipts/${id}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ reason }),
    });
    return handleResponse(response);
  },

  async uploadPhotoBatch(id, files) {
    const formData = new FormData();
    files.forEach(f => formData.append('photos', f));
    const response = await fetch(`${API_BASE}/receipts/${id}/photo`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: formData,
    });
    return handleResponse(response);
  },

  async exportAllReceipts(filter = 'all', filterId = '', filterConcept = '', filterPayer = '', filterRecipient = '', dateFrom = '', dateTo = '', projectId = null) {
    let url = `${API_BASE}/receipts/export?filter=${filter}`;
    if (filterId) url += `&id=${encodeURIComponent(filterId)}`;
    if (filterConcept) url += `&concept=${encodeURIComponent(filterConcept)}`;
    if (filterPayer) url += `&payer=${encodeURIComponent(filterPayer)}`;
    if (filterRecipient) url += `&recipient=${encodeURIComponent(filterRecipient)}`;
    if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
    if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;
    if (projectId) url += `&project_id=${projectId}`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return response.text();
  },

  async exportPooledReceipts(filter = 'all', filterId = '', dateFrom = '', dateTo = '', concepts = [], projectId = null) {
    let url = `${API_BASE}/receipts/export-pooled?filter=${filter}`;
    if (filterId) url += `&id=${encodeURIComponent(filterId)}`;
    if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
    if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;
    if (concepts.length > 0) url += `&concepts=${encodeURIComponent(concepts.join(','))}`;
    if (projectId) url += `&project_id=${projectId}`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return response.text();
  },

  async verifyReceipt(id) {
    const response = await fetch(`${API_BASE}/receipts/${id}/verify`);
    return response.text();
  },

  // Contacts
  async getContacts(projectId = null) {
    let url = `${API_BASE}/contacts`;
    if (projectId) url += `?project_id=${projectId}`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  async createContact(contact, projectId = null) {
    // contact is {name, type, phone?, email?, country?}. The previous
    // (name, type) signature still works for callers that pass only
    // those — the rest default to undefined and the API ignores them.
    let url = `${API_BASE}/contacts`;
    if (projectId) url += `?project_id=${projectId}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(contact),
    });
    return handleResponse(response);
  },

  async deleteContact(id) {
    const response = await fetch(`${API_BASE}/contacts/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },

  // updateContact partially edits an existing contact (name/phone/email).
  // Each field is *string on the backend, so omit a key to leave it
  // unchanged and pass '' to clear it.
  async updateContact(id, data) {
    const response = await fetch(`${API_BASE}/contacts/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // Export the selected project's contacts as a JSON download
  // (Content-Disposition: attachment; filename=...). Optional `type`
  // filters to "sender" / "recipient" / "concept"; omit to export
  // all three types. Mirrors the backend's GET /api/contacts/export.
  async exportContacts(projectId, type = '') {
    let url = `${API_BASE}/contacts/export?project_id=${projectId}`;
    if (type) url += `&type=${encodeURIComponent(type)}`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    if (!response.ok) await handleResponse(response);
    // Return both the parsed JSON and the filename from the
    // Content-Disposition header so the caller can drive the
    // download with the same name the server generated.
    const text = await response.text();
    let filename = 'contacts.json';
    const cd = response.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename=([^;]+)/);
    if (m) filename = m[1].replace(/^"|"$/g, '');
    return { data: JSON.parse(text), filename };
  },

  // Import a previously-exported contacts JSON into the selected
  // project. `contacts` is the array of contact objects to import.
  // The handler dedups by case-insensitive name+type within the
  // target project and returns a summary {created, skipped, errors}.
  async importContacts(projectId, contacts) {
    const response = await fetch(`${API_BASE}/contacts/import?project_id=${projectId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ contacts }),
    });
    return handleResponse(response);
  },

  // Delivery channels (Phase 1)
  async sendReceiptEmail(id) {
    const response = await fetch(`${API_BASE}/receipts/${id}/send-email`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    return handleResponse(response);
  },

  async getWhatsAppLink(id) {
    const response = await fetch(`${API_BASE}/receipts/${id}/whatsapp-link`, {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    return handleResponse(response);
  },

  // createDownloadLink mints a one-shot, 14-day-expiry download URL for
  // the receipt. The backend invalidates any previously-active link for
  // the same receipt so the latest "Send via WhatsApp" click is the
  // only one that works. Returns:
  //   url       — the public link the payer opens (e.g. https://.../api/download/<token>)
  //   token     — the random token ID (also embedded in url)
  //   expires_at— ISO timestamp (now + 14 days)
  //   phone     — the receipt's payer phone (already resolved)
  //   wa_url    — pre-built wa.me URL with the download URL in the message
  // Caller can either open wa_url directly (one-click "open WhatsApp
  // with this message ready to send") or compose their own.
  async createDownloadLink(id) {
    const response = await fetch(`${API_BASE}/receipts/${id}/download-link`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    return handleResponse(response);
  },
};

// ============ Public config ============
// No auth required. Returns {publicUrl, companyName, waDefaultCc}.
// Used by the Copy Verification URL button and the public verify page.
export const getPublicConfig = async () => {
  const response = await fetch(`${API_BASE}/config`);
  return handleResponse(response);
};

// ============ Quote API ============
export const createQuote = async (quote, projectId) => {
  const token = localStorage.getItem('token');
  const payload = { ...quote, project_id: projectId };
  const response = await fetch(`${API_BASE}/quotes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(payload)
  });
  return handleResponse(response);
};

export const getQuotes = async (projectId, status = '', page = 1, limit = 20, sort = 'date_desc', quoteType = '') => {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams({ project_id: projectId, page: String(page), limit: String(limit), sort });
  if (status) params.append('status', status);
  if (quoteType) params.append('quote_type', quoteType);
  const response = await fetch(`${API_BASE}/quotes?${params}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return handleResponse(response);
};

export const getQuote = async (uuid) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE}/quotes/${uuid}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return handleResponse(response);
};

export const updateQuote = async (uuid, quote) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE}/quotes/${uuid}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(quote)
  });
  return handleResponse(response);
};

export const deleteQuote = async (uuid) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE}/quotes/${uuid}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return handleResponse(response);
};

export const acceptQuote = async (uuid) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE}/quotes/${uuid}/accept`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return handleResponse(response);
};

export const rejectQuote = async (uuid) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE}/quotes/${uuid}/reject`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return handleResponse(response);
};

export const getMyProjects = async () => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE}/projects/my`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return handleResponse(response);
};
